import { useMutation as useBaseMutation } from "@tanstack/react-query";
import { axiosClient } from "./axios";
import { queryClient } from "./components/Provider";
import type {
  Body,
  Data,
  EndpointPath,
  Errors,
  Result,
  UNEXPECTED_ERROR,
} from "./types";

export const useMutation = <Path extends EndpointPath>(options: {
  path: Path;
  dependencies?: EndpointPath[];
  onSuccess?: (data: Data<Path>) => any;
  onError?: (err: Errors<Path>) => any;
}) => {
  const response = useBaseMutation<Result<Path>, UNEXPECTED_ERROR, Body<Path>>({
    mutationFn: async (params) => {
      const response = await axiosClient.post(options.path, params ?? {});
      return response.data;
    },
    onSuccess: (data) => {
      if (data && data.ok) {
        queryClient.invalidateQueries({
          queryKey: options?.dependencies ?? [],
        });
        options?.onSuccess?.(data.data);
      } else {
        options?.onError?.(data.error);
      }
    },
    onError: (err) => {
      options?.onError?.(err);
    },
  });
  if (response.isIdle) {
    return {
      loading: false as const,
      error: undefined,
      data: undefined,
      mutate: response.mutate as typeof response.mutate,
    };
  } else if (response.isPending) {
    return {
      loading: true as const,
      error: undefined,
      data: undefined,
      mutate: response.mutate as typeof response.mutate,
    };
  } else if (response.error) {
    return {
      loading: false as const,
      error: response.error as Errors<Path>,
      data: undefined,
      mutate: response.mutate as typeof response.mutate,
    };
  } else if (!response.data) {
    return {
      loading: false as const,
      error: {
        code: "UNDEFINED_DATA_RESPONSE",
        message: "UNDEFINED_DATA_RESPONSE",
      } as Errors<Path>,
      data: undefined,
      mutate: response.mutate as typeof response.mutate,
    };
  } else if (!response.data.ok) {
    return {
      loading: false as const,
      error: response.data.error as Errors<Path>,
      data: undefined,
      mutate: response.mutate as typeof response.mutate,
    };
  } else {
    return {
      loading: false as const,
      error: undefined,
      data: response.data.data as Data<Path>,
      mutate: response.mutate as typeof response.mutate,
    };
  }
};
